// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {AsyncButtonEl} from 'common/components/async-button';
import {BannerEl} from 'common/components/banner';
import {ButtonEl} from 'common/components/button';
import {InputEl} from 'common/components/input';
import {ModalEl} from 'common/components/modal';
import {SelectEl} from 'common/components/select';
import {BannerAction, BannerType} from 'common/components/banner/types';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {getEntityTypeName} from 'common/report/util/saved-entities';
import {SwitchEl} from 'common/components/switch';
import {InputLabelEl} from 'common/components/input/label';

import {SharingBannerEl} from '../sharing/banner';
import type {CreateEntityModal} from './index';

export function template(this: CreateEntityModal) {
  const {description, globalAccessType, name, saving, isCompanyKey, companyKeyName} = this.state;
  const entityTypeNamePlural = this.getContext(`i18n`).print(
    getEntityTypeName(this.attr(`entity-type`), {
      plural: true,
    }),
  );

  const NameInput = (props: {label?: string; placeHolderValue?: string} = {}) => (
    <InputEl
      attrs={{
        placeholder:
          props?.placeHolderValue ?? this.t(`components.create-entity.untitled`, {context: this.attr(`entity-type`)}),
        label: props?.label ?? this.t(`components.create-entity.name`),
        'validation-status': this.getNameError() ? `invalid` : ``,
        'error-message': this.getNameError() || ``,
        elref: `entityNameInput`,
        disabled: this.helpers.isSavingRestricted(),
      }}
      props={{
        value: name,
      }}
      on={{
        input: this.helpers.handleNameInput,
        keydown: this.helpers.handleKeydown,
      }}
      hook={{
        insert: ({elm}) => this.setElRef(`nameInput`, elm as HTMLElement),
        destroy: () => this.setElRef(`nameInput`, null),
      }}
    />
  );

  const InputDescription = ({
    text,
    learnMoreLink,
    alignFlexStart,
  }: {
    text: string;
    learnMoreLink?: string;
    alignFlexStart?: boolean;
  }) => (
    <div sel={`.input-description${alignFlexStart ? `-align-flex-start` : ``}`}>
      <div sel='.input-description-text'>{text ? text : ``}</div>
      {learnMoreLink ? (
        <a
          sel={`.input-description-learn-more${alignFlexStart ? `-align-flex-start` : ``}`}
          attrs={{
            href: learnMoreLink,
            target: `_blank`,
          }}
        >
          {this.t(`common.learn-more`)}
        </a>
      ) : null}
    </div>
  );

  return (
    <ModalEl
      sel='.modal.create-entity-modal'
      attrs={{
        open: this.attr(`open`),
        role: `dialog`,
        title: this.attr(`modal-title`),
        closeable: this.attr(`closeable`),
        'modal-title': this.attr(`modal-title`),
        'modal-description': `Modal for managing ${entityTypeNamePlural}`,
      }}
      on={{change: this.helpers.handleModalChange}}
    >
      <div sel='.mp-card-header'>
        <div sel='.mp-card-header-title'>{this.attr(`modal-title`)}</div>
      </div>
      <div sel='.mp-modal-content'>
        {this.helpers.limitBannerRenderer()}
        {this.helpers.isSavingRestricted() ? (
          <BannerEl
            sel='.banner'
            attrs={{
              theme: `info`,
              type: BannerType.Embedded,
              'header-text': this.t(`components.create-entity.cohort-save-upgrade`),
              'body-text': this.t(`components.create-entity.cohort-save-upgrade-description`),
              ctas: [{text: this.t(`common.upgrade`), icon: `share`, actionName: BannerAction.Upgrade}],
              'style-override': `.banner-wrapper .banner-panel .banner-copy {width: calc(100% - 130px)}`,
            }}
            on={{
              action: this.helpers.openPricingUpsell,
            }}
          />
        ) : (
          <>
            {!this.attr(`show-global-access-menu`) || this.helpers.userLacksSharePermission() ? (
              <SharingBannerEl
                sel='.banner'
                attrs={{
                  'project-id': this.attr(`project-id`),
                  'entity-type': this.attr(`entity-type`),
                  'sub-entity-type': this.attr(`sub-entity-type`),
                }}
              />
            ) : null}
            {this.attr(`warning`) ? (
              <BannerEl
                sel='.banner'
                attrs={{
                  theme: `warn`,
                  type: BannerType.Embedded,
                  'body-text': this.attr(`warning`),
                }}
              />
            ) : null}
          </>
        )}
        {this.attr(`entity-type`) === `data group` ? (
          <>
            <NameInput
              label={this.t(`components.create-entity.group-property`)}
              placeHolderValue={this.t(`components.create-entity.group-property-placeholder`)}
            />
            <InputDescription
              text={this.t(`components.create-entity.group-property-description`)}
              learnMoreLink='https://help.mixpanel.com/hc/en-us/articles/360025333632#implementation'
            />
            <InputEl
              attrs={this.helpers.getDataGroupDescriptionInputAttrs()}
              props={{value: description}}
              on={{
                input: this.helpers.handleDescriptionInput,
                keydown: this.helpers.handleKeydown,
              }}
              hook={{
                insert: ({elm}) => this.setElRef(`descriptionTextarea`, elm as HTMLElement),
                destroy: () => this.setElRef(`descriptionTextarea`, null),
              }}
            />
            {this.helpers.isCompanyProfileEnabled() ? (
              <>
                <InputDescription text={this.t(`components.create-entity.group-description`)} />
                <div sel='.company-metadata-container'>
                  <div sel='.company-metadata-description-container'>
                    <InputLabelEl
                      attrs={{
                        label: `Set as Company key`,
                      }}
                    />
                    <InputDescription
                      text={`Enables company revenue analytics and company profiles.`}
                      learnMoreLink='https://help.mixpanel.com/hc/en-us/articles/360025333632#implementation'
                      alignFlexStart
                    />
                  </div>
                  <SwitchEl attrs={{on: isCompanyKey}} on={{change: this.helpers.handleIsCompanyKeyChange}} />
                </div>
                {isCompanyKey ? (
                  <>
                    <InputEl
                      attrs={this.helpers.getDataGroupCompanyNameInputAttrs()}
                      props={{value: companyKeyName}}
                      on={{
                        input: this.helpers.handleCompanyKeyNameInput,
                        keydown: this.helpers.handleKeydown,
                      }}
                    />
                    <InputDescription text={this.t(`components.edit-data-group.company-name-description`)} />
                  </>
                ) : null}
              </>
            ) : null}
          </>
        ) : (
          <>
            <NameInput />
            <InputEl
              attrs={this.helpers.getDescriptionInputAttrs()}
              props={{value: description}}
              on={{
                input: this.helpers.handleDescriptionInput,
                keydown: this.helpers.handleKeydown,
              }}
              hook={{
                insert: ({elm}) => this.setElRef(`descriptionTextarea`, elm as HTMLElement),
                destroy: () => this.setElRef(`descriptionTextarea`, null),
              }}
            />
          </>
        )}
        {this.helpers.showGlobalAccessMenu() ? (
          <div sel='.global-access-container'>
            <div sel='.label'>{this.t(`components.create-entity.who-can-see`)}</div>
            <SelectEl
              sel='.global-access-select'
              attrs={{
                'selected-option': globalAccessType,
                'label-transform': `uppercase`,
                size: ButtonSize.Large,
                elref: `globalAccessMenu`,
              }}
              props={{
                sections: this.helpers.getGlobalAccessTypeSections(),
              }}
              on={{
                change: this.helpers.handleGlobalAccessTypeChange,
              }}
            />
          </div>
        ) : null}
        <div sel='.modal-footer'>
          <div sel='.modal-actions'>
            {this.attr(`show-share-management`) && !this.attr(`show-copy-link-button`) ? (
              <div sel='.modal-actions-confirm-share-container'>
                <ButtonEl
                  attrs={{
                    theme: ButtonTheme.Secondary,
                    disabled: !this.helpers.formIsValid() || Boolean(saving),
                    elref: `saveAndShareButton`,
                  }}
                  on={{
                    click: () => this.helpers.saveChanges(true),
                  }}
                >
                  {this.helpers.showGlobalAccessMenu()
                    ? this.t(`components.create-entity.customize-access`)
                    : this.t(`components.create-entity.save-and-share`)}
                </ButtonEl>
              </div>
            ) : null}
            {this.attr(`show-copy-link-button`) && !this.attr(`show-share-management`) ? (
              <div sel='.modal-actions-confirm-share-container'>
                <AsyncButtonEl
                  params={{
                    asyncAction: this.helpers.copyLink,
                    theme: ButtonTheme.Secondary,
                    elref: `copyLinkButton`,
                    icon: `link`,
                  }}
                >
                  {this.t(`common.copy-url`)}
                </AsyncButtonEl>
              </div>
            ) : null}
            <div sel='.modal-actions-cancel-container'>
              <ButtonEl
                sel='.modal-actions-cancel'
                attrs={{
                  theme: ButtonTheme.Tertiary,
                  elref: `cancelButton`,
                }}
                on={{
                  click: this.helpers.handleSaveModalCancel,
                }}
              >
                {this.t(`common.cancel`)}
              </ButtonEl>
            </div>
            <div sel='.modal-actions-confirm-container' on={{click: this.helpers.setSubmitAttempted}}>
              <ButtonEl
                sel='.modal-actions-confirm'
                attrs={{
                  theme: ButtonTheme.Primary,
                  disabled: !this.helpers.formIsValid() || Boolean(saving),
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  icon: saving ? `spinner` : ``,
                  elref: `saveEntityButton`,
                }}
                on={{
                  click: () => this.helpers.saveChanges(false),
                }}
              >
                {
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  saving ? this.t(`common.saving`) : this.t(`common.save`)
                }
              </ButtonEl>
            </div>
          </div>
        </div>
      </div>
    </ModalEl>
  );
}
